'use client';

import { c as _c } from "react/compiler-runtime";
import type { ProductCard } from '@repo/shopify';
import useEmblaCarousel from 'embla-carousel-react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { ProductCardClient } from './product-card-client';

/**
 * `<Link>` only prefetches once its element intersects the viewport, and the
 * Embla viewport is `overflow-hidden`, so every slide past the first screenful
 * stays un-prefetched until it is swiped into view — and then the (serial
 * `/_tree` → page) prefetch starts while the newly revealed images are already
 * competing for bandwidth. Measured on a 1.6 Mbps / 150 ms profile: a slide
 * revealed by a swipe had its page ready ~1.7 s later, so a swipe-and-tap
 * navigated with no feedback. Instead, once the carousel itself is on screen
 * (and the network is idle), warm every slide's per-URL static shell via
 * `router.prefetch` — same CDN-cached payload the `<Link>` would fetch, so the
 * two dedupe in the segment cache; no per-link server render.
 */
function usePrefetchAllSlides(emblaApi, hrefs) {
  const $ = _c(7);
  const router = useRouter();
  let t0;
  if ($[0] !== hrefs) {
    t0 = hrefs.join("\n");
    $[0] = hrefs;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const hrefKey = t0;
  let t1;
  let t2;
  if ($[2] !== emblaApi || $[3] !== hrefKey || $[4] !== router) {
    t1 = () => {
      const root = emblaApi?.rootNode();
      if (!root || typeof IntersectionObserver === "undefined") {
        return;
      }
      const connection = (navigator as Navigator & {
        connection?: {
          saveData?: boolean;
          effectiveType?: string;
        };
      }).connection;
      if (connection?.saveData || /2g/.test(connection?.effectiveType ?? "")) {
        return;
      }
      let cancelled = false;
      let idle = null;
      const prefetchAll = () => {
        if (cancelled) {
          return;
        }
        for (const href of hrefKey.split("\n")) {
          if (href) {
            router.prefetch(href);
          }
        }
      };
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(_temp)) {
          return;
        }
        observer.disconnect();
        idle = typeof requestIdleCallback === "function" ? requestIdleCallback(prefetchAll, {
          timeout: 1500
        }) : setTimeout(prefetchAll, 300);
      }, {
        rootMargin: "200px"
      });
      observer.observe(root);
      return () => {
        cancelled = true;
        observer.disconnect();
        if (idle !== null) {
          if (typeof cancelIdleCallback === "function" && typeof idle === "number") {
            cancelIdleCallback(idle);
          } else {
            clearTimeout(idle as ReturnType<typeof setTimeout>);
          }
        }
      };
    };
    t2 = [emblaApi, hrefKey, router];
    $[2] = emblaApi;
    $[3] = hrefKey;
    $[4] = router;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
}
function _temp(entry) {
  return entry.isIntersecting;
}
interface ProductCarouselProps {
  products: ProductCard[];
  showQuickAdd?: boolean;
  priorityImageCount?: number;
  enableReviews?: boolean;
  moneyLocale?: string;
  ratings?: (number | null)[];
  reviewCounts?: (number | null)[];
}
const carouselImageSizes = '(min-width: 1024px) 25vw, (min-width: 768px) 33vw, (min-width: 640px) 45vw, 70vw';
export function ProductCarousel(t0) {
  const $ = _c(44);
  const {
    products,
    showQuickAdd: t1,
    priorityImageCount: t2,
    enableReviews: t3,
    moneyLocale,
    ratings,
    reviewCounts
  } = t0;
  const showQuickAdd = t1 === undefined ? true : t1;
  const priorityImageCount = t2 === undefined ? 0 : t2;
  const enableReviews = t3 === undefined ? false : t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      align: "start",
      dragFree: true,
      loop: false,
      skipSnaps: false,
      slidesToScroll: "auto",
      containScroll: "trimSnaps",
      watchDrag: true,
      watchResize: true,
      watchSlides: true,
      duration: 25
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(t4);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [paginationReady, setPaginationReady] = useState(false);
  let t5;
  if ($[1] !== products) {
    t5 = products.map(_temp2);
    $[1] = products;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  usePrefetchAllSlides(emblaApi, t5);
  let t6;
  if ($[3] !== emblaApi) {
    t6 = () => emblaApi?.scrollPrev();
    $[3] = emblaApi;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const scrollPrev = t6;
  let t7;
  if ($[5] !== emblaApi) {
    t7 = () => emblaApi?.scrollNext();
    $[5] = emblaApi;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const scrollNext = t7;
  let t8;
  if ($[7] !== emblaApi) {
    t8 = () => {
      if (!emblaApi) {
        return;
      }
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
      const snapCount = emblaApi.scrollSnapList().length;
      const selectedSnap = emblaApi.selectedScrollSnap();
      setTotalPages(snapCount);
      setActiveIndex(selectedSnap);
      setPaginationReady(true);
    };
    $[7] = emblaApi;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const onSelect = t8;
  let t10;
  let t9;
  if ($[9] !== emblaApi || $[10] !== onSelect) {
    t9 = () => {
      if (!emblaApi) {
        return;
      }
      onSelect();
      emblaApi.on("select", onSelect);
      emblaApi.on("reInit", onSelect);
      return () => {
        emblaApi.off("select", onSelect);
        emblaApi.off("reInit", onSelect);
      };
    };
    t10 = [emblaApi, onSelect];
    $[9] = emblaApi;
    $[10] = onSelect;
    $[11] = t10;
    $[12] = t9;
  } else {
    t10 = $[11];
    t9 = $[12];
  }
  useEffect(t9, t10);
  if (products.length === 0) {
    return null;
  }
  let t11;
  if ($[13] !== activeIndex || $[14] !== canScrollNext || $[15] !== canScrollPrev || $[16] !== paginationReady || $[17] !== scrollNext || $[18] !== scrollPrev || $[19] !== totalPages) {
    t11 = paginationReady && totalPages > 1 && <div className="pointer-events-none absolute -top-12 right-0 z-10 flex items-center gap-4 md:-top-14"><div className="pointer-events-auto flex items-center gap-2 text-sm text-neutral-500"><button type="button" onClick={scrollPrev} disabled={!canScrollPrev} className="transition-colors hover:text-neutral-900 disabled:opacity-30" aria-label="Previous products"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true"><title>Previous</title><path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" /></svg></button><span>{activeIndex + 1} / {totalPages}</span><button type="button" onClick={scrollNext} disabled={!canScrollNext} className="transition-colors hover:text-neutral-900 disabled:opacity-30" aria-label="Next products"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true"><title>Next</title><path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" /></svg></button></div></div>;
    $[13] = activeIndex;
    $[14] = canScrollNext;
    $[15] = canScrollPrev;
    $[16] = paginationReady;
    $[17] = scrollNext;
    $[18] = scrollPrev;
    $[19] = totalPages;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== enableReviews || $[22] !== moneyLocale || $[23] !== priorityImageCount || $[24] !== products || $[25] !== ratings || $[26] !== reviewCounts || $[27] !== showQuickAdd) {
    let t13;
    if ($[29] !== enableReviews || $[30] !== moneyLocale || $[31] !== priorityImageCount || $[32] !== ratings || $[33] !== reviewCounts || $[34] !== showQuickAdd) {
      t13 = (product_0, index) => <div key={product_0.id} className="min-w-0 shrink-0 basis-[70%] pl-3 sm:basis-[45%] md:basis-1/3 md:pl-4 lg:basis-1/4"><ProductCardClient product={product_0} showQuickAdd={showQuickAdd} preloadPrimaryImage={index < priorityImageCount} imageSizes={carouselImageSizes} enableReviews={enableReviews} moneyLocale={moneyLocale} rating={ratings?.[index] ?? undefined} reviewCount={reviewCounts?.[index] ?? undefined} /></div>;
      $[29] = enableReviews;
      $[30] = moneyLocale;
      $[31] = priorityImageCount;
      $[32] = ratings;
      $[33] = reviewCounts;
      $[34] = showQuickAdd;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    t12 = products.map(t13);
    $[21] = enableReviews;
    $[22] = moneyLocale;
    $[23] = priorityImageCount;
    $[24] = products;
    $[25] = ratings;
    $[26] = reviewCounts;
    $[27] = showQuickAdd;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[36] !== t12) {
    t13 = <div className="-ml-3 flex md:-ml-4">{t12}</div>;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== emblaRef || $[39] !== t13) {
    t14 = <div ref={emblaRef} className="overflow-hidden cursor-grab active:cursor-grabbing">{t13}</div>;
    $[38] = emblaRef;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t11 || $[42] !== t14) {
    t15 = <div className="relative">{t11}{t14}</div>;
    $[41] = t11;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  return t15;
}
function _temp2(product) {
  return `/products/${product.handle}`;
}